import CommentsAPI from "../api";
import { showError, showConsoleError } from "../../util";


const MUTED_USERS = "muted_users";
const actions = {
  // Este método inicializa el hilo de comentarios. Para ello guarda en el store
  // el id del contenido para usarlo posteriormente en las distintas operaciones
  // de recuperación de comentarios. Sólo se llama una vez en el método `mounted`
  // del componente `EdiComments`.
  initContentCommentThread({ dispatch, commit }, contentId) {
    commit('INIT_CONTENT_COMMENT_THREAD', contentId);
    return Promise.all(
      [
        dispatch('refreshComments'),
        dispatch('fetchMutedUsers'),
        dispatch('fetchUserLikedComments')
      ]
    )
  },
  // Reset permite controlar si el listado de comentarios mantiene los resultados
  // o por el contrario lo reinicia, por ejemplo, si filtramos por orden queremos
  // mantener el orden, pero si visitamos la siguiente página, queremos mantener
  // los comentarios que ya hay pintados
  async fetchComments({ state, commit }, reset = true) {
    const { comments, totalComments, pages, page } = await CommentsAPI.fetchComments(state.contentId, state.filters).catch(showConsoleError.bind(this));
    commit('SET_COMMENTS', { comments, totalComments, pages, page, reset });
  },
  async fetchFeaturedComment({ state, commit }) {
    const { comment } = await CommentsAPI.fetchFeaturedComment(state.contentId).catch(showConsoleError.bind(this));
    commit('SET_FEATURED_COMMENT', { comment });
  },
  async fetchPartnerComments({ state, commit }) {
    const { comments } = await CommentsAPI.fetchPartnerComments(state.contentId).catch(showConsoleError.bind(this));
    commit('SET_PARTNER_COMMENTS', { comments });
  },
  async createComment({ commit, state, dispatch }, text) {
    const { comment } = await CommentsAPI.createComment(state.contentId, text, state.replyingCommentId).catch(showError.bind(this));
    dispatch('refreshComments');
    if (comment.state === "pendingModeration") {
      console.debug("Comment is pending moderation!")
      this._vm.$toasted.global.commentModerated();
    }
    // Si el comentario es en respuesta a otro, limpiamos el estado
    // referente al usuario/comentario que se está respondiendo pues ya no es
    // necesaria esa info hasta el próximo "responder a"
    if (state.replyingCommentId) {
      dispatch('clearReplyingUser');
    }
  },
  refreshComments({ dispatch }) {
    return Promise.all(
      [
        dispatch('fetchComments'),
        dispatch('fetchFeaturedComment'),
        dispatch('fetchPartnerComments')
      ]
    );
  },
  async votes({ commit, state }, { commentId, isPositive }) {
    // Sólo pedimos los datos si aún no lo hemos pedido, igual esto es problematico pero
    // el yo del futuro se encargará. Si no es el yo y es el tú, lo siento.
    if (!state.commentUserLikes[commentId]) {
      const users = await CommentsAPI.votes(commentId, isPositive).catch(showConsoleError.bind(this));
      commit('SET_COMMENT_USERS_VOTES', { users, commentId });
    }
  },
  async vote({ dispatch }, { commentId, contentId, isPositive = true }) {
    if (isPositive) {
      window.dataLayer.push({
        event: 'comment_interaction',
        eventData: {
          im_action: 'me_gusta',
          content_id: contentId.toString()
        }
      });
    }
    await CommentsAPI.vote(commentId, isPositive).catch(showError.bind(this));
    dispatch('refreshComments');
    dispatch('fetchUserLikedComments');
  },
  async report({ dispatch }, commentId) {
    await CommentsAPI.report(commentId).catch(showError.bind(this));
    dispatch('refreshComments');
  },
  async fetchMutedUsers({ commit, getters }) {
    if (!getters.isLogged) {
      localStorage.removeItem(MUTED_USERS);
      return false;
    }

    let storedMutedUsers = localStorage.getItem(MUTED_USERS);

    let listMutedUsers = {},
      expirationTime = null,
      mutedUsersFromStorage = null;

    if (storedMutedUsers) {
      listMutedUsers = JSON.parse(storedMutedUsers);
      expirationTime = listMutedUsers.exp;
      mutedUsersFromStorage = listMutedUsers.mutedUsers;
    }

    if (
      (expirationTime && listMutedUsers) &&
      ((new Date().getTime()) < expirationTime)
    ) {
      commit('SET_MUTED_USERS', { mutedUsers: mutedUsersFromStorage });

      return true;
    }

    const mutedUsers = await CommentsAPI.mutedUsers().catch(showConsoleError.bind(this));

    if (mutedUsers) {
      localStorage.setItem(MUTED_USERS, JSON.stringify({
        mutedUsers: mutedUsers,
        exp: (new Date().getTime() + (1000 * 60 * 60))
      }));
      commit('SET_MUTED_USERS', { mutedUsers });
    }
  },
  async muteUser({ dispatch }, userId ) {
    await CommentsAPI.muteUser(userId).catch(showError.bind(this));

    let storedMutedUsers = localStorage.getItem(MUTED_USERS);
    if (storedMutedUsers) {
      let listMutedUsers = JSON.parse(storedMutedUsers);

      if (typeof listMutedUsers.mutedUsers === "undefined") {
        listMutedUsers.mutedUsers = [];
      }
      if (!listMutedUsers.mutedUsers.includes(userId)) {
        listMutedUsers.mutedUsers.push(userId);
      }

      localStorage.setItem(MUTED_USERS, JSON.stringify({
        mutedUsers: listMutedUsers.mutedUsers,
        exp: listMutedUsers.exp || (new Date().getTime() + (1000 * 60 * 60))
      }));
    }
    dispatch('fetchMutedUsers');
  },
  async unmuteUser({ dispatch }, {userId, userNick}) {
    await CommentsAPI.unmuteUser(userId).catch(showError.bind(this));
    this._vm.$toasted.global.unmuteUser({ message: userNick });

    let storedMutedUsers = localStorage.getItem(MUTED_USERS);
    if (storedMutedUsers) {
      let listMutedUsers = JSON.parse(storedMutedUsers);
      // Eliminamos el usuario de la lista sin alterar la fecha de expiracion de la clave
      if (typeof listMutedUsers.mutedUsers !== "undefined") {
        listMutedUsers.mutedUsers = listMutedUsers.mutedUsers.filter((storedUserId) => (storedUserId !== userId));

        localStorage.setItem(MUTED_USERS, JSON.stringify({
          mutedUsers: listMutedUsers.mutedUsers,
          exp: listMutedUsers.exp || (new Date().getTime() + (1000 * 60 * 60))
        }));
      }
    }
    dispatch('fetchMutedUsers');
  },
  replyUser({ commit }, { userId, userNick, commentId }) {
    commit('SET_REPLYING_USER', { userId, userNick, commentId });
  },
  clearReplyingUser({ commit }) {
    commit('CLEAR_REPLYING_USER');
  },
  updateFilters({ commit, dispatch, state }, filters) {
    // Sólo actualizamos si el filtro cambia. Fundamentalmente
    // esta situación se puede dar cuando seleccionas el filtro
    // por el que ya estás ordenando. Ej: el valor por defecto es 'Relevancia' y
    // eliges 'Relevancia', así en ese caso no realizaríamos la búsqueda
    if (JSON.stringify(filters) !== JSON.stringify(state.filters)) {
      commit('UPDATE_COMMENT_FILTERS', { filters });
      dispatch('fetchComments', !filters.page || filters.page === 1);
    }
  },
  async fetchUserLikedComments({ commit, getters }) {
    if (getters.isLogged) {
      const commentsId = await CommentsAPI.userLikedCommentIds().catch(showConsoleError.bind(this));
      commit('SET_USER_LIKED_COMMENTS', { commentsId });
    }
  }
};

export default actions;
